import React, { useState } from 'react';
import Navigation from './Navigation';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { 
  Calendar,
  Send,
  Mail,
  Phone,
  Shield,
  Clock,
  UserCheck,
  MapPin,
  CheckCircle,
  MessageCircle,
  Building2,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    role: '',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    
    try {
      // Client-side validation
      const validationErrors = [];
      
      if (!formData.fullName || formData.fullName.trim().length < 2) {
        validationErrors.push('Full name must be at least 2 characters long');
      }
      
      if (!formData.email || formData.email.trim() === '') {
        validationErrors.push('Email address is required');
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email.trim())) {
          validationErrors.push('Please enter a valid email address');
        }
      }
      
      if (!formData.company || formData.company.trim().length < 2) {
        validationErrors.push('Company name must be at least 2 characters long');
      }
      
      if (!formData.role || formData.role.trim() === '') {
        validationErrors.push('Please select your role');
      }
      
      if (!formData.message || formData.message.trim().length < 10) {
        validationErrors.push('Message must be at least 10 characters long');
      }
      
      if (formData.phone && formData.phone.trim()) {
        const phoneRegex = /^[\+]?[1-9][\d\s\-\(\)]{7,20}$/;
        if (!phoneRegex.test(formData.phone.trim())) {
          validationErrors.push('Please enter a valid phone number');
        }
      }
      
      if (validationErrors.length > 0) {
        throw new Error(`Please fix the following issues:\n• ${validationErrors.join('\n• ')}`);
      }

      // Check if backend URL is available
      if (!BACKEND_URL) {
        throw new Error('Service is temporarily unavailable. Please try again later or email us directly at info@vioqbase.com');
      }

      const contactData = {
        full_name: formData.fullName.trim(),
        email: formData.email.trim().toLowerCase(),
        company: formData.company.trim(),
        role: formData.role.trim(),
        phone: formData.phone.trim(),
        message: formData.message.trim()
      };

      // Set up request timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      const response = await fetch(`${BACKEND_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorMessage = 'Failed to submit contact form. Please try again.';
        
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch (parseError) {
          // Use default error message if response isn't JSON
        }

        if (response.status === 400) {
          // Don't modify validation error messages from backend
        } else if (response.status === 500) {
          errorMessage = 'Server error. Please try again in a few minutes or email us directly at info@vioqbase.com';
        } else if (response.status === 429) {
          errorMessage = 'Too many requests. Please wait a moment and try again.';
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      if (result.success) {
        setSubmitSuccess(true);
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          company: '',
          role: '',
          phone: '',
          message: ''
        });
      } else {
        throw new Error(result.message || 'Contact form submission failed');
      }
      
    } catch (error) {
      console.error('Contact form submission error:', error);
      
      let userFriendlyMessage = error.message;
      
      if (error.name === 'AbortError') {
        userFriendlyMessage = 'Request timed out. Please check your internet connection and try again.';
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        userFriendlyMessage = 'Unable to connect to our servers. Please check your internet connection or email us directly at info@vioqbase.com';
      } else if (!navigator.onLine) {
        userFriendlyMessage = 'You appear to be offline. Please check your internet connection and try again.';
      }
      
      setSubmitError(userFriendlyMessage);
      
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDemo = () => {
    window.open('https://cal.com/smartaisolutions/vioqbase-demo', '_blank');
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navigation />
      
      {/* Page Hero */}
      <section className="relative bg-gradient-to-br from-[#112d56] via-[#1a3d6b] to-[#112d56] text-white py-20 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
        <div className="relative container mx-auto px-6 text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
              Let's <span className="text-[#ff7a1f]">Connect</span>
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 leading-relaxed">
              Whether you have a question, want a custom quote, or need support — we're here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-12">
              
              {/* Main Contact Form */}
              <div className="lg:col-span-2">
                <Card className="border-0 shadow-xl bg-white">
                  <CardHeader className="pb-8">
                    <CardTitle className="text-3xl font-bold text-[#112d56] flex items-center space-x-3">
                      <MessageCircle className="h-8 w-8 text-[#ff7a1f]" />
                      <span>Send Us a Message</span>
                    </CardTitle>
                    <p className="text-lg text-gray-600 mt-4">
                      Fill out the form below and our team will get back to you promptly.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="fullName" className="text-sm font-semibold text-gray-700">
                            Full Name *
                          </Label>
                          <Input
                            id="fullName"
                            type="text"
                            placeholder="Enter your full name"
                            value={formData.fullName}
                            onChange={(e) => handleInputChange('fullName', e.target.value)}
                            className="border-2 border-gray-200 focus:border-[#ff7a1f] focus:ring-[#ff7a1f]/20 rounded-lg py-3 px-4 text-base"
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-sm font-semibold text-gray-700">
                            Work Email Address *
                          </Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="you@company.com"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className="border-2 border-gray-200 focus:border-[#ff7a1f] focus:ring-[#ff7a1f]/20 rounded-lg py-3 px-4 text-base"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="company" className="text-sm font-semibold text-gray-700">
                            Company Name *
                          </Label>
                          <Input
                            id="company"
                            type="text"
                            placeholder="Your company name"
                            value={formData.company}
                            onChange={(e) => handleInputChange('company', e.target.value)}
                            className="border-2 border-gray-200 focus:border-[#ff7a1f] focus:ring-[#ff7a1f]/20 rounded-lg py-3 px-4 text-base"
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="role" className="text-sm font-semibold text-gray-700">
                            Which VioSuite solution are you interested in? *
                          </Label>
                          <Select onValueChange={(value) => handleInputChange('role', value)} required>
                            <SelectTrigger className="border-2 border-gray-200 focus:border-[#ff7a1f] focus:ring-[#ff7a1f]/20 rounded-lg py-3 px-4 text-base h-12">
                              <SelectValue placeholder="Select a VioSuite solution" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="vioqbase">VioQbase - Voice-Powered Knowledge Base</SelectItem>
                              <SelectItem value="vioreceptionist">VioReceptionist - AI Voice Receptionist</SelectItem>
                              <SelectItem value="vioconcierge">VioConcierge - Appointment Management</SelectItem>
                              <SelectItem value="viocoach">VioCoach - Executive Coaching Platform</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm font-semibold text-gray-700">
                          Phone Number (Optional)
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="+44 20 1234 5678"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className="border-2 border-gray-200 focus:border-[#ff7a1f] focus:ring-[#ff7a1f]/20 rounded-lg py-3 px-4 text-base"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-sm font-semibold text-gray-700">
                          What can we help you with? *
                        </Label>
                        <Textarea
                          id="message"
                          placeholder="Tell us about your needs, questions, or how we can help your team..."
                          rows={4}
                          value={formData.message}
                          onChange={(e) => handleInputChange('message', e.target.value)}
                          className="border-2 border-gray-200 focus:border-[#ff7a1f] focus:ring-[#ff7a1f]/20 rounded-lg py-3 px-4 text-base resize-none"
                          required
                        />
                      </div>

                      {/* Success/Error Messages */}
                      {submitSuccess && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                            <div>
                              <p className="text-green-800 font-semibold">Thank you! Your message has been sent.</p>
                              <p className="text-green-700 text-sm">We'll get back to you within 24 hours. Check your email for confirmation.</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {submitError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-center space-x-2">
                            <div className="h-5 w-5 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-xs font-bold">!</span>
                            </div>
                            <div>
                              <p className="text-red-800 font-semibold">Submission failed</p>
                              <p className="text-red-700 text-sm">{submitError}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <Button
                        type="submit"
                        size="lg"
                        disabled={isSubmitting}
                        className="w-full md:w-auto bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-8 py-4 text-lg rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:scale-100"
                      >
                        {isSubmitting ? (
                          <div className="flex items-center space-x-2">
                            <Loader2 className="h-5 w-5 animate-spin" />
                            <span>Sending...</span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <Mail className="h-5 w-5" />
                            <span>Send Message</span>
                          </div>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-8">
                
                {/* Direct Contact Details */}
                <Card className="border-0 shadow-lg bg-white">
                  <CardHeader className="pb-6">
                    <CardTitle className="text-2xl font-bold text-[#112d56]">Direct Contact</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <a 
                      href="mailto:support@smartaisolutions.ai"
                      className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                    >
                      <div className="w-10 h-10 bg-[#ff7a1f]/10 rounded-lg flex items-center justify-center group-hover:bg-[#ff7a1f]/20 transition-colors">
                        <Mail className="h-5 w-5 text-[#ff7a1f]" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Support</p>
                        <p className="text-gray-600">support@smartaisolutions.ai</p>
                      </div>
                    </a>

                    <a 
                      href="mailto:sales@smartaisolutions.ai"
                      className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                    >
                      <div className="w-10 h-10 bg-[#ff7a1f]/10 rounded-lg flex items-center justify-center group-hover:bg-[#ff7a1f]/20 transition-colors">
                        <Building2 className="h-5 w-5 text-[#ff7a1f]" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Sales</p>
                        <p className="text-gray-600">sales@smartaisolutions.ai</p>
                      </div>
                    </a>


                  </CardContent>
                </Card>

                {/* Trust Section */}
                <Card className="border-0 shadow-lg bg-white">
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-start space-x-3">
                      <Shield className="h-5 w-5 text-[#ff7a1f] mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-700">Your data is encrypted and secure.</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <UserCheck className="h-5 w-5 text-[#ff7a1f] mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-700">We'll never share your information.</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Clock className="h-5 w-5 text-[#ff7a1f] mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-700">Average response time: within 24 hours.</p>
                    </div>
                  </CardContent>
                </Card>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <MapPin className="h-6 w-6 text-[#ff7a1f]" />
              <p className="text-lg text-gray-700">
                Based in <span className="font-semibold text-[#112d56]">London, UK</span> — supporting enterprise teams globally.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final Encouragement */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <p className="text-xl text-gray-700 leading-relaxed">
              Not sure what you need? That's okay — <span className="font-semibold text-[#112d56]">let's talk and find the right solution together.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Footer - Added VioQbase footer */}
      <footer className="bg-[#112d56] text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center space-x-2 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_vioq-content-update/artifacts/w40y3694_Smart%20AI%20Logo%20white.png"
                  alt="Smart AI Solutions"
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-blue-200 leading-relaxed mb-8 max-w-xs">
                Driving efficiency and customer excellence with VioSuite — AI-powered voice & knowledge solutions for modern business.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Product</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/features" className="hover:text-[#ff7a1f] transition-colors text-lg">Features</a></li>
                <li><a href="/use-cases" className="hover:text-[#ff7a1f] transition-colors text-lg">Use Cases</a></li>
                <li><a href="/products/concierge" className="hover:text-[#ff7a1f] transition-colors text-lg">Smart Parrot Concierge</a></li>
                <li><a href="/products/viocoach" className="hover:text-[#ff7a1f] transition-colors text-lg">VioCoach</a></li>
                <li><a href="https://cal.com/smartaisolutions/vioqbase-demo" target="_blank" rel="noopener noreferrer" className="hover:text-[#ff7a1f] transition-colors text-lg">Book Demo</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Company</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/about" className="hover:text-[#ff7a1f] transition-colors text-lg">About</a></li>
                <li><a href="/contact" className="hover:text-[#ff7a1f] transition-colors text-lg">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Legal</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a></li>
                <li><a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          
          <Separator className="bg-blue-800/50 my-8" />
          
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-blue-200 text-lg">
              © 2025 VioQbase. All rights reserved.
            </p>
            <div className="flex space-x-8 text-blue-200 mt-4 md:mt-0">
              <a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a>
              <a href="/terms" className="hover:text-[#ff7a1f] transition-colors text-lg">Terms of Service</a>
              <a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;